/**
 * Tracks signed approvals / receipts from beneficiaries (stored in settlement frame data).
 */

import { useCallback, useEffect, useState } from 'react';
import { useSettlementSession } from '../../hooks/useSettlementSession';

const FRAME_ID = 'sframe-close-beneficiary-approvals';

export interface BeneficiaryRow {
  id: string;
  name: string;
  status: 'pending' | 'approved';

}

export interface BeneficiaryApprovalTrackerProps {
  estateId: string | null;
  readOnly?: boolean;
}

function newRow(): BeneficiaryRow {
  return {
    id: typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `b-${Date.now()}`,
    name: '',
    status: 'pending',
  };
}

export default function BeneficiaryApprovalTracker({ estateId, readOnly }: BeneficiaryApprovalTrackerProps) {
  const { loadFrameData, saveFrameData, isLoading } = useSettlementSession(estateId, { readOnly });
  const [rows, setRows] = useState<BeneficiaryRow[]>([]);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!estateId) return;
    (async () => {
      const row = await loadFrameData(FRAME_ID);
      const data = (row?.data || {}) as { beneficiaries?: BeneficiaryRow[] };
      if (Array.isArray(data.beneficiaries) && data.beneficiaries.length > 0) {
        setRows(data.beneficiaries);
      } else {
        setRows([newRow()]);
      }
      setDirty(false);
    })();
  }, [estateId, loadFrameData]);

  const persist = useCallback(async () => {
    if (!estateId || readOnly) return;
    await saveFrameData(FRAME_ID, { beneficiaries: rows, updatedAt: new Date().toISOString() });
    setDirty(false);
  }, [estateId, readOnly, rows, saveFrameData]);

  useEffect(() => {
    if (!dirty || readOnly || !estateId) return;
    const t = setTimeout(() => {
      void persist();
    }, 600);
    return () => clearTimeout(t);
  }, [dirty, persist, readOnly, estateId, rows]);

  const updateRow = (id: string, patch: Partial<BeneficiaryRow>) => {
    if (readOnly) return;
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    setDirty(true);
  };

  const addRow = () => {
    if (readOnly) return;
    setRows((prev) => [...prev, newRow()]);
    setDirty(true);
  };

  const removeRow = (id: string) => {
    if (readOnly) return;
    setRows((prev) => (prev.filter((r) => r.id !== id)));
    setDirty(true);
  };

  const allNamedHaveReceipt = rows.every((r) => !r.name.trim() || r.status === 'approved');
  const hasAnyName = rows.some((r) => r.name.trim().length > 0);

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-4 space-y-4">
      <div>
        <h4 className="text-sm font-semibold text-slate-900">Beneficiary approval tracker</h4>
        <p className="text-xs text-slate-600 mt-1">
          Record each beneficiary who must approve the final accounting or sign a receipt. Mark when documentation is
          approved.
        </p>
      </div>

      {rows.length > 0 && (
        <div className="space-y-2">
          {rows.map((r) => (
            <div key={r.id} className="flex items-center gap-3 bg-white border border-slate-200 rounded-lg px-3 py-2">
              <input
                className="flex-1 min-w-0 border border-slate-300 rounded-md px-2 py-1.5 text-sm"
                value={r.name}
                disabled={readOnly}
                onChange={(e) => updateRow(r.id, { name: e.target.value })}
                placeholder="Beneficiary name"
              />
              <div className="flex shrink-0 rounded-md border border-slate-200 overflow-hidden text-xs font-medium">
                <button
                  type="button"
                  disabled={readOnly}
                  onClick={() => updateRow(r.id, { status: 'pending' })}
                  className={`px-3 py-1.5 transition-colors ${
                    r.status === 'pending'
                      ? 'bg-amber-100 text-amber-900'
                      : 'bg-white text-slate-500 hover:bg-slate-50'
                  } ${readOnly ? 'cursor-not-allowed' : ''}`}
                >
                  Pending
                </button>
                <button
                  type="button"
                  disabled={readOnly}
                  onClick={() => updateRow(r.id, { status: 'approved' })}
                  className={`px-3 py-1.5 border-l border-slate-200 transition-colors ${
                    r.status === 'approved'
                      ? 'bg-emerald-100 text-emerald-900'
                      : 'bg-white text-slate-500 hover:bg-slate-50'
                  } ${readOnly ? 'cursor-not-allowed' : ''}`}
                >
                  Approved
                </button>
              </div>
              {!readOnly && (
                <button type="button" className="text-xs text-red-600 hover:underline shrink-0" onClick={() => removeRow(r.id)}>
                  Remove
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {!readOnly && (
        <button type="button" onClick={addRow} className="text-sm text-slate-700 underline">
        + Add beneficiary
        </button>
      )}

      <div className={`text-xs rounded-md p-2 ${hasAnyName && allNamedHaveReceipt ? 'bg-emerald-50 text-emerald-900' : 'bg-amber-50 text-amber-900'}`}>
        {hasAnyName && allNamedHaveReceipt
          ? 'All listed beneficiaries have signed documentation recorded.'
          : 'List each beneficiary and check "Approved" when you have their signed approval or receipt.'}
      </div>

      {isLoading && <p className="text-xs text-slate-500">Saving…</p>}
    </div>
  );
}
